import { isDisallowedUserAgent } from '@root/utils/mobileHelpers';
import getConfig from 'next/config';
import Link from 'next/link';
import { Loader } from 'old-stylus-ui';
import { useEffect } from 'react';
import { CursiveRecorder, isElectron } from 'utils/electron';
import GoogleOauthButton from 'v2/components/buttons/GoogleOauthButton';

import { AuthenticationFormProps } from '../../../../utils/authentication';
import Button from '../../atoms/Button';
import Input from '../../atoms/Input';
import MessageBanner from '../../atoms/MessageBanner';
import GoogleButton from '../../buttons/GoogleButton';

const { publicRuntimeConfig } = getConfig();

const HAS_GOOGLE_SETTINGS = publicRuntimeConfig.googleOauthClientId && publicRuntimeConfig.googleOauthRedirectUrl;

const LoginForm = ({
  email,
  onEmailChange,
  password,
  onPasswordChange,
  otp,
  onOtpChange,
  onSubmit,
  onSsoSubmit,
  showOtp,
  loading,
  ssoCheckLoading,
  error,
  showPassword,
  disableSubmit,
  ssoType,
  hideSubmit,
  redirectToDesktop,
  backToEmbed,
  isSigningInWithSso
}: AuthenticationFormProps) => {
  let buttonText = 'Continue';

  if (showOtp) {
    buttonText = 'Sign in with Code';
  } else if (ssoType === 'forced') {
    buttonText = 'Sign in with SSO';
  } else if (showPassword) {
    buttonText = 'Sign in with Email';
  }

  useEffect(() => {
    localStorage.removeItem('userSignedOut');
  }, []);

  if (isSigningInWithSso) {
    return (
      <div className='sm:px-auto mt-0 flex w-full flex-col items-center justify-center'>
        <p className='mb-4 mt-0 text-center text-lg text-slate-700'>Authenticating your Scribe account...</p>
        <Loader />
      </div>
    );
  }

  return (
    <div className='sm:px-auto mt-0 flex w-full flex-col items-center justify-center px-4'>
      <p className='mb-4 mt-0 text-center text-lg text-slate-700 sm:mb-10'>Sign in to Scribe</p>
      {ssoType !== 'forced' && HAS_GOOGLE_SETTINGS && !isDisallowedUserAgent() && (
        <>
          {!isElectron() ? (
            <GoogleOauthButton buttonWidth='304' buttonType='signin_with' redirectToDesktop={redirectToDesktop} backToEmbed={backToEmbed} />
          ) : (
            <GoogleButton
              className='w-full'
              onClick={() => CursiveRecorder().openURLInBrowser(`${publicRuntimeConfig.cursiveRootUrl}signin?redirect_to_desktop=true`)}
              type='signin'
            />
          )}
          <div className='relative mb-8 mt-10 flex w-full flex-col'>
            <div className='border border-slate-200' />
            <span className='absolute -top-2 order-1 flex self-center bg-white px-4 text-xs font-medium text-slate-500'>
              or sign in using email
            </span>
          </div>
        </>
      )}
      {ssoType === 'forced' && (
        <span className='mb-4 text-sm font-normal leading-5 text-slate-600'>Your company requires an SSO provider for signing in.</span>
      )}

      <form
        className='w-full'
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Input
          data-testid='emailField'
          type='email'
          id='email'
          label='Email Address'
          autoFocus
          value={email}
          className='w-full'
          onChange={(e) => onEmailChange(e.target.value)}
          aria-label='signin-email'
        />

        {ssoType !== 'forced' && showPassword && !showOtp && (
          <div className='mt-4 flex w-full'>
            <Input
              autoFocus
              label='Password'
              type='password'
              id='password'
              containerClassName='w-full'
              value={password}
              data-testid='passwordField'
              onChange={(e) => onPasswordChange(e.target.value)}
            />
          </div>
        )}
        {showOtp && (
          <div className='mt-4 flex w-full'>
            <Input
              autoFocus
              label='Code'
              type='text'
              className='w-full'
              value={otp}
              data-testid='otpField'
              onChange={(e) => onOtpChange(e.target.value)}
            />
          </div>
        )}
        {!hideSubmit && (
          <>
            <Button
              data-testid='submitButton'
              disabled={disableSubmit || ssoCheckLoading}
              variant={disableSubmit ? 'secondary' : 'primary'}
              className='mt-10 w-full'
              type='submit'
            >
              {buttonText}
            </Button>
            {ssoType === 'optional' && (
              <Button onClick={onSsoSubmit} variant='secondary' data-testid='signInIdp' className='mt-2 w-full'>
                Sign in with SSO
              </Button>
            )}
          </>
        )}
      </form>
      {(loading || ssoCheckLoading) && (
        <div className='mt-4 flex h-8 w-full items-center justify-center' data-testid='loader'>
          <Loader size='sm' />
        </div>
      )}
      <MessageBanner title={error} variant='error' className='mb-2 mt-2 w-full' />

      <div className='mt-5 flex flex-col items-center justify-center'>
        {ssoType !== 'forced' && (
          <span className='mb-4 text-xs font-medium text-slate-500'>
            Forgot your password?{' '}
            <Link href='/reset-password' className='cursor-pointer text-indigo-600 hover:text-indigo-700'>
              Reset it.
            </Link>
          </span>
        )}
        <span className='text-xs font-medium text-slate-500'>
          No Account?{' '}
          <Link href='/signup' className='cursor-pointer text-indigo-600 hover:text-indigo-700'>
            Sign up.
          </Link>
        </span>
      </div>
    </div>
  );
};

export default LoginForm;
